/**
 * WikiEdit — Simplified wiki edit page for LEXIS entries.
 * Route: /wiki/:id/edit
 * One source of truth: kb_entries table. Editors save → live immediately.
 * Admin and Editor roles only.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../auth/api';
import { useAuth } from '../../auth/useAuth';
import RoleGate from '../../auth/RoleGate';
import TagChips from '../../components/wiki/TagChips';
import { ArrowLeft, Save, CheckCircle, Plus, X, Search } from 'lucide-react';

export default function WikiEdit() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const entryId = parseInt(id, 10);

    // State
    const [entry, setEntry] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState('');
    const [categories, setCategories] = useState([]);
    const [history, setHistory] = useState([]);
    const [showHistory, setShowHistory] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);

    // Form data
    const [formData, setFormData] = useState({
        title: '',
        overview_en: '',
        overview_vi: '',
        capabilities_en: '',
        capabilities_vi: '',
        client_fit: null,
        internal_notes: '',
        category_id: null,
        tags: [],
        related_entry_ids: [],
    });

    // Tag input
    const [tagInput, setTagInput] = useState('');
    const tagInputRef = useRef(null);

    // Related entry search
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);

    // Tab state for EN/VI
    const [overviewTab, setOverviewTab] = useState('en');

    // Load entry data
    const loadEntry = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await api.get(`/wiki/entries/${entryId}/edit`);
            const data = res.data;
            setEntry(data);
            setFormData({
                title: data.title || '',
                overview_en: data.overview_en || '',
                overview_vi: data.overview_vi || '',
                capabilities_en: data.capabilities_en || '',
                capabilities_vi: data.capabilities_vi || '',
                client_fit: data.client_fit,
                internal_notes: data.internal_notes || '',
                category_id: data.category_id,
                tags: data.tags || [],
                related_entry_ids: data.related_entry_ids || [],
            });
        } catch (e) {
            setError('Failed to load entry: ' + (e.response?.data?.detail || e.message));
        } finally {
            setLoading(false);
        }
    }, [entryId]);

    // Load categories
    const loadCategories = useCallback(async () => {
        try {
            const res = await api.get('/wiki/categories');
            setCategories(res.data?.categories || []);
        } catch (e) {
            console.error('Failed to load categories:', e);
        }
    }, []);

    // Load history
    const loadHistory = useCallback(async () => {
        try {
            const res = await api.get(`/wiki/entries/${entryId}/history`);
            setHistory(res.data || []);
        } catch (e) {
            console.error('Failed to load history:', e);
        }
    }, [entryId]);

    useEffect(() => {
        loadEntry();
        loadCategories();
    }, [loadEntry, loadCategories]);

    // Unsaved changes warning
    useEffect(() => {
        if (!hasChanges) return;
        const handler = (e) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [hasChanges]);

    // Search for related entries (debounced)
    useEffect(() => {
        if (!searchQuery.trim()) {
            setSearchResults([]);
            return;
        }
        setSearching(true);
        const timer = setTimeout(async () => {
            try {
                const res = await api.get(`/wiki/search?q=${encodeURIComponent(searchQuery)}`);
                const results = res.data?.results || [];
                // Filter out current entry and already-related entries
                const filtered = results.filter(
                    r => r.id !== entryId && !formData.related_entry_ids.includes(r.id)
                );
                setSearchResults(filtered.slice(0, 5));
            } catch (e) {
                console.error('Search failed:', e);
            } finally {
                setSearching(false);
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [searchQuery, entryId, formData.related_entry_ids]);

    // Handle field change
    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        setHasChanges(true);
        setSaved(false);
    };

    // Add bullet to capabilities textarea
    const insertBullet = (field) => {
        const textarea = document.querySelector(`textarea[name="${field}"]`);
        if (!textarea) return;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const text = formData[field];
        const before = text.substring(0, start);
        const after = text.substring(end);
        const needsNewline = before.length > 0 && !before.endsWith('\n') ? '\n' : '';
        const newText = before + needsNewline + '• ' + after;
        handleChange(field, newText);
        // Set cursor position after bullet
        setTimeout(() => {
            textarea.focus();
            textarea.selectionStart = textarea.selectionEnd = start + needsNewline.length + 2;
        }, 0);
    };

    // Tag management
    const addTag = (e) => {
        e.preventDefault();
        const tag = tagInput.trim().toLowerCase();
        if (!tag || formData.tags.includes(tag)) {
            setTagInput('');
            return;
        }
        handleChange('tags', [...formData.tags, tag]);
        setTagInput('');
        if (tagInputRef.current) tagInputRef.current.focus();
    };

    const removeTag = (tag) => {
        handleChange('tags', formData.tags.filter(t => t !== tag));
    };

    // Related entry management
    const addRelatedEntry = (entry) => {
        handleChange('related_entry_ids', [...formData.related_entry_ids, entry.id]);
        setSearchQuery('');
        setSearchResults([]);
    };

    const removeRelatedEntry = (entryId) => {
        handleChange('related_entry_ids', formData.related_entry_ids.filter(id => id !== entryId));
    };

    // Save
    const handleSave = async () => {
        setSaving(true);
        setError('');
        try {
            await api.put(`/wiki/entries/${entryId}`, formData);
            setSaved(true);
            setHasChanges(false);
            setTimeout(() => setSaved(false), 2000);
            // Reload entry to get updated data
            loadEntry();
            loadHistory();
        } catch (e) {
            setError('Save failed: ' + (e.response?.data?.detail || e.message));
        } finally {
            setSaving(false);
        }
    };

    // Rollback to revision (admin only)
    const handleRollback = async (revisionId) => {
        if (!confirm('Are you sure you want to rollback to this revision? This cannot be undone.')) return;
        try {
            await api.post(`/wiki/entries/${entryId}/rollback/${revisionId}`);
            loadEntry();
            loadHistory();
        } catch (e) {
            alert('Rollback failed: ' + (e.response?.data?.detail || e.message));
        }
    };

    if (loading) {
        return <div className="loading"><div className="spinner" />Loading entry...</div>;
    }

    if (error && !entry) {
        return (
            <div style={{ textAlign: 'center', padding: '4rem' }}>
                <h2 style={{ color: 'var(--error)' }}>Error</h2>
                <p style={{ color: 'var(--text-secondary)' }}>{error}</p>
                <button onClick={() => navigate(-1)} style={{
                    background: 'var(--bg-secondary)', color: 'var(--text-primary)',
                    padding: '8px 16px', borderRadius: '6px', border: 'none', cursor: 'pointer',
                }}>← Back</button>
            </div>
        );
    }

    // Get related entry titles for display
    const relatedEntryTitles = entry?.related_entries
        ? entry.related_entries.map(re => re.title || re.title_en || `Entry #${re.id}`)
        : [];

    return (
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            {/* HEADER */}
            <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <button onClick={() => navigate(`/wiki/entry/${entry?.slug || entryId}`)} style={{
                        background: 'none', border: 'none', color: 'var(--text-secondary)',
                        cursor: 'pointer', fontSize: '1.2rem', padding: '4px',
                    }}>
                        <ArrowLeft size={20} />
                    </button>
                    <div>
                        <h1 style={{ margin: 0, fontSize: '1.5rem', color: 'var(--text-primary)' }}>
                            Edit: {formData.title || 'Untitled'}
                        </h1>
                        {entry?.last_edited_by && (
                            <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                                Last edited by {entry.last_edited_by} at {entry.last_edited_at ? new Date(entry.last_edited_at).toLocaleString() : 'N/A'}
                            </p>
                        )}
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    {saved && (
                        <span style={{ color: 'var(--success)', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <CheckCircle size={14} /> Saved
                        </span>
                    )}
                    <button onClick={handleSave} disabled={saving || !hasChanges} style={{
                        background: saving || !hasChanges ? 'var(--bg-tertiary)' : 'var(--gold)',
                        color: saving || !hasChanges ? 'var(--text-secondary)' : 'var(--bg-primary)',
                        padding: '8px 20px', borderRadius: '6px', border: 'none',
                        cursor: saving || !hasChanges ? 'not-allowed' : 'pointer',
                        fontWeight: 600, fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '6px',
                    }}>
                        <Save size={16} />
                        {saving ? 'Saving...' : 'Save'}
                    </button>
                </div>
            </div>

            {error && (
                <div style={{
                    background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
                    borderRadius: '8px', padding: '12px', marginBottom: '1rem', color: '#ef4444',
                }}>
                    {error}
                </div>
            )}

            {/* 1. BASIC INFO */}
            <SectionCard title="Basic Info">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                        <label style={labelStyle}>Title</label>
                        <input
                            type="text"
                            value={formData.title}
                            onChange={e => handleChange('title', e.target.value)}
                            style={inputStyle}
                        />
                    </div>
                    <div>
                        <label style={labelStyle}>Category</label>
                        <select
                            value={formData.category_id || ''}
                            onChange={e => handleChange('category_id', e.target.value ? parseInt(e.target.value, 10) : null)}
                            style={inputStyle}
                        >
                            <option value="">— Select category —</option>
                            {categories.map(cat => (
                                <option key={cat.id} value={cat.id}>{cat.name}</option>
                            ))}
                        </select>
                    </div>
                </div>
                <div style={{ marginTop: '0.75rem' }}>
                    <span style={{
                        background: entry?.status === 'published' ? 'rgba(34,197,94,0.15)' : 'rgba(245,158,11,0.15)',
                        color: entry?.status === 'published' ? '#22c55e' : '#f59e0b',
                        padding: '2px 10px', borderRadius: '99px', fontSize: '0.75rem', fontWeight: 600,
                    }}>
                        {entry?.status === 'published' ? 'Published' : 'Draft'} — saves will go live immediately
                    </span>
                </div>
            </SectionCard>

            {/* 2. OVERVIEW */}
            <SectionCard title="Overview">
                <TabToggle value={overviewTab} onChange={setOverviewTab} tabs={[
                    { key: 'en', label: 'EN' },
                    { key: 'vi', label: 'VI' },
                ]} />
                {overviewTab === 'en' && (
                    <textarea
                        name="overview_en"
                        value={formData.overview_en}
                        onChange={e => handleChange('overview_en', e.target.value)}
                        rows={6}
                        style={textareaStyle}
                        placeholder="Describe the product in English..."
                    />
                )}
                {overviewTab === 'vi' && (
                    <textarea
                        name="overview_vi"
                        value={formData.overview_vi}
                        onChange={e => handleChange('overview_vi', e.target.value)}
                        rows={6}
                        style={textareaStyle}
                        placeholder="Mô tả sản phẩm bằng tiếng Việt..."
                    />
                )}
            </SectionCard>

            {/* 3. CAPABILITIES */}
            <SectionCard title="Capabilities">
                <div style={{ marginBottom: '0.5rem' }}>
                    <label style={labelStyle}>Capabilities (EN)</label>
                    <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <button onClick={() => insertBullet('capabilities_en')} style={{
                            background: 'var(--bg-tertiary)', border: '1px solid var(--border)',
                            color: 'var(--text-primary)', padding: '4px 10px', borderRadius: '4px',
                            cursor: 'pointer', fontSize: '0.8rem',
                        }}>
                            Add bullet
                        </button>
                    </div>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
                        Each capability on a new line starting with •
                    </p>
                    <textarea
                        name="capabilities_en"
                        value={formData.capabilities_en}
                        onChange={e => handleChange('capabilities_en', e.target.value)}
                        rows={6}
                        style={textareaStyle}
                        placeholder="• Capability 1&#10;• Capability 2"
                    />
                </div>
                <div style={{ marginTop: '1rem' }}>
                    <label style={labelStyle}>Capabilities (VI)</label>
                    <textarea
                        name="capabilities_vi"
                        value={formData.capabilities_vi}
                        onChange={e => handleChange('capabilities_vi', e.target.value)}
                        rows={4}
                        style={textareaStyle}
                        placeholder="Khả năng bằng tiếng Việt..."
                    />
                </div>
            </SectionCard>

            {/* 4. CLIENT FIT */}
            <SectionCard title="Client Fit">
                <textarea
                    value={typeof formData.client_fit === 'string' ? formData.client_fit : JSON.stringify(formData.client_fit, null, 2)}
                    onChange={e => {
                        try {
                            handleChange('client_fit', JSON.parse(e.target.value));
                        } catch {
                            handleChange('client_fit', e.target.value);
                        }
                    }}
                    rows={4}
                    style={textareaStyle}
                    placeholder="Client fit JSON..."
                />
                <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.75rem' }}>
                    Client match data (kb_entry_clients) is managed by the enricher and shown below:
                </p>
                {entry?.kb_entry_clients && entry.kb_entry_clients.length > 0 && (
                    <div style={{ marginTop: '0.75rem' }}>
                        {entry.kb_entry_clients.map((client, i) => (
                            <div key={i} style={{
                                background: 'var(--bg-tertiary)', borderRadius: '6px', padding: '10px',
                                marginBottom: '0.5rem', fontSize: '0.85rem',
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                                        {client.code_name}
                                    </span>
                                    <span style={{ color: client.fit_score >= 8 ? '#22c55e' : client.fit_score >= 6 ? '#f59e0b' : '#ef4444', fontWeight: 700 }}>
                                        {client.fit_score}/10
                                    </span>
                                </div>
                                {client.fit_reason && (
                                    <p style={{ margin: '4px 0 0', color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                                        {client.fit_reason}
                                    </p>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </SectionCard>

            {/* 5. INTERNAL NOTES (admin only) */}
            <RoleGate roles={['admin']}>
                <SectionCard title="Internal Notes (Admin Only)">
                    <textarea
                        value={formData.internal_notes}
                        onChange={e => handleChange('internal_notes', e.target.value)}
                        rows={4}
                        style={textareaStyle}
                        placeholder="Internal notes..."
                    />
                </SectionCard>
            </RoleGate>

            {/* 6. TAGS */}
            <SectionCard title="Tags">
                <form onSubmit={addTag} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                    <input
                        ref={tagInputRef}
                        type="text"
                        value={tagInput}
                        onChange={e => setTagInput(e.target.value)}
                        placeholder="Type tag + Enter to add"
                        style={{ ...inputStyle, flex: 1 }}
                    />
                    <button type="submit" style={{
                        background: 'var(--bg-tertiary)', border: '1px solid var(--border)',
                        color: 'var(--text-primary)', padding: '6px 12px', borderRadius: '4px',
                        cursor: 'pointer',
                    }}>
                        <Plus size={16} />
                    </button>
                </form>
                {formData.tags.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                        {formData.tags.map((tag, i) => (
                            <span key={i} style={{
                                background: 'rgba(148,163,184,0.15)', color: '#94a3b8',
                                padding: '0.2rem 0.5rem', borderRadius: '9999px', fontSize: '0.75rem',
                                display: 'flex', alignItems: 'center', gap: '4px',
                            }}>
                                {tag}
                                <button onClick={() => removeTag(tag)} style={{
                                    background: 'none', border: 'none', color: 'inherit',
                                    cursor: 'pointer', padding: 0, fontSize: '0.85rem', lineHeight: 1,
                                }}>
                                    ×
                                </button>
                            </span>
                        ))}
                    </div>
                )}
            </SectionCard>

            {/* 7. RELATED ENTRIES */}
            <SectionCard title="Related Entries">
                <div style={{ position: 'relative', marginBottom: '0.75rem' }}>
                    <div style={{ position: 'relative' }}>
                        <Search size={16} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            placeholder="Search entries by title..."
                            style={{ ...inputStyle, paddingLeft: '32px' }}
                        />
                    </div>
                    {searching && <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '4px' }}>Searching...</p>}
                    {searchResults.length > 0 && (
                        <div style={{
                            background: 'var(--bg-secondary)', border: '1px solid var(--border)',
                            borderRadius: '6px', marginTop: '0.5rem', maxHeight: '200px', overflowY: 'auto',
                        }}>
                            {searchResults.map(result => (
                                <div key={result.id} onClick={() => addRelatedEntry(result)} style={{
                                    padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid var(--border)',
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                }}>
                                    <span style={{ color: 'var(--text-primary)', fontSize: '0.9rem' }}>
                                        {result.title_en || result.title}
                                    </span>
                                    {result.category?.name && (
                                        <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                                            {result.category.name}
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                {formData.related_entry_ids.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                        {formData.related_entry_ids.map((relId, i) => (
                            <span key={relId} style={{
                                background: 'rgba(99,102,241,0.15)', color: '#6366f1',
                                padding: '0.2rem 0.5rem', borderRadius: '9999px', fontSize: '0.75rem',
                                display: 'flex', alignItems: 'center', gap: '4px',
                            }}>
                                {relatedEntryTitles[i] || `Entry #${relId}`}
                                <button onClick={() => removeRelatedEntry(relId)} style={{
                                    background: 'none', border: 'none', color: 'inherit',
                                    cursor: 'pointer', padding: 0, fontSize: '0.85rem', lineHeight: 1,
                                }}>
                                    ×
                                </button>
                            </span>
                        ))}
                    </div>
                )}
            </SectionCard>

            {/* 8. REVISION HISTORY */}
            <SectionCard title="Revision History" defaultOpen={false}>
                <button onClick={() => { setShowHistory(!showHistory); if (!showHistory) loadHistory(); }} style={{
                    background: 'none', border: 'none', color: 'var(--text-secondary)',
                    cursor: 'pointer', fontSize: '0.85rem', marginBottom: '0.75rem',
                }}>
                    {showHistory ? 'Hide' : 'Show'} revision history
                </button>
                {showHistory && history.length > 0 && (
                    <div>
                        {history.map(rev => (
                            <div key={rev.id} style={{
                                padding: '10px', borderBottom: '1px solid var(--border)',
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                fontSize: '0.85rem',
                            }}>
                                <div>
                                    <div style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                                        {rev.edited_by_name || 'Unknown'}
                                    </div>
                                    <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                                        {rev.edited_at ? new Date(rev.edited_at).toLocaleString() : 'N/A'}
                                    </div>
                                    {rev.fields_changed && rev.fields_changed.length > 0 && (
                                        <div style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', marginTop: '2px' }}>
                                            Changed: {rev.fields_changed.join(', ')}
                                        </div>
                                    )}
                                </div>
                                <RoleGate roles={['admin']}>
                                    <button onClick={() => handleRollback(rev.id)} style={{
                                        background: 'rgba(239,68,68,0.15)', color: '#ef4444',
                                        border: '1px solid rgba(239,68,68,0.3)', borderRadius: '4px',
                                        padding: '4px 10px', fontSize: '0.75rem', cursor: 'pointer',
                                    }}>
                                        Rollback
                                    </button>
                                </RoleGate>
                            </div>
                        ))}
                    </div>
                )}
                {showHistory && history.length === 0 && (
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>No revision history available.</p>
                )}
            </SectionCard>
        </div>
    );
}

// Sub-components

function SectionCard({ title, children, defaultOpen = true }) {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div style={{
            background: 'rgba(30,41,59,0.8)', borderRadius: '12px', padding: '20px',
            border: '1px solid rgba(148,163,184,0.1)', marginBottom: '16px',
        }}>
            <h3
                onClick={() => setOpen(!open)}
                style={{
                    color: 'var(--gold)', fontSize: '0.9rem', marginTop: 0, marginBottom: open ? '12px' : 0,
                    cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}
            >
                {title}
                <span style={{ fontSize: '0.75rem' }}>{open ? '▾' : '▸'}</span>
            </h3>
            {open && children}
        </div>
    );
}

function TabToggle({ value, onChange, tabs }) {
    return (
        <div style={{ display: 'flex', gap: '0', marginBottom: '0.75rem', width: 'fit-content' }}>
            {tabs.map(tab => (
                <button
                    key={tab.key}
                    onClick={() => onChange(tab.key)}
                    style={{
                        background: value === tab.key ? 'var(--gold)' : 'var(--bg-tertiary)',
                        color: value === tab.key ? 'var(--bg-primary)' : 'var(--text-secondary)',
                        border: '1px solid var(--border)',
                        padding: '4px 14px', fontSize: '0.8rem', fontWeight: 600,
                        cursor: 'pointer',
                        borderRadius: tab.key === tabs[0].key ? '4px 0 0 4px' : '0 4px 4px 0',
                    }}
                >
                    {tab.label}
                </button>
            ))}
        </div>
    );
}

const labelStyle = {
    display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)',
    marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px',
};

const inputStyle = {
    width: '100%', background: 'var(--bg-tertiary)', border: '1px solid var(--border)',
    borderRadius: '6px', padding: '8px 12px', color: 'var(--text-primary)',
    fontSize: '0.9rem', outline: 'none',
};

const textareaStyle = {
    width: '100%', background: 'var(--bg-tertiary)', border: '1px solid var(--border)',
    borderRadius: '6px', padding: '8px 12px', color: 'var(--text-primary)',
    fontSize: '0.9rem', outline: 'none', resize: 'vertical', fontFamily: 'inherit',
};
